import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Badge, Row, Col, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CommandEditor from '../components/CommandEditor';
import { botApi } from '../api/api';

export default function BotSettings() {
  const [settings, setSettings] = useState(null);
  const [botStatus, setBotStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchStatus = () => botApi.status().then((r) => setBotStatus(r.data)).catch(() => {});

  useEffect(() => {
    botApi.getSettings().then((r) => setSettings(r.data)).catch(() => {});
    fetchStatus();
    // Poll status every 5s to pick up external changes
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await botApi.updateSettings(settings);
      setSettings(data);
      toast.success('Einstellungen gespeichert!');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const toggleBot = async () => {
    setToggling(true);
    try {
      if (botStatus?.running) {
        await botApi.stop();
        toast.info('Bot wird gestoppt…');
      } else {
        await botApi.start();
        toast.success('Bot wird gestartet…');
      }
      // Give the worker a moment to process
      setTimeout(() => { fetchStatus(); setToggling(false); }, 2000);
    } catch {
      toast.error('Fehler beim Starten/Stoppen');
      setToggling(false);
    }
  };

  if (!settings) return <div className="text-muted">Lade…</div>;

  const isRunning = botStatus?.running;

  return (
    <div>
      {/* Header with big Start/Stop button */}
      <Card bg="dark" text="white" className="border-secondary mb-4">
        <Card.Body className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div>
            <h4 className="text-white mb-1">🤖 Twitch Bot</h4>
            <p className="text-muted small mb-0">
              Startet den Twitch-Chat-Bot und den Spotify-Poller für deinen Channel.
              Stelle vorher sicher, dass Spotify und Twitch verbunden sind.
            </p>
          </div>
          <div className="d-flex align-items-center gap-3">
            <Badge bg={isRunning ? 'success' : 'secondary'} className="fs-6 px-3 py-2">
              {isRunning ? '● Aktiv' : '○ Gestoppt'}
            </Badge>
            <Button
              variant={isRunning ? 'danger' : 'success'}
              size="lg"
              onClick={toggleBot}
              disabled={toggling}
              style={{ minWidth: 140 }}
            >
              {toggling ? '⏳ Bitte warten…' : isRunning ? '⏹ Bot stoppen' : '▶ Bot starten'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {!isRunning && (
        <Alert variant="info" className="small">
          <strong>Voraussetzungen für den Bot-Start:</strong><br />
          ① Spotify-Konto verbunden → ② Twitch Haupt-Konto verbunden →
          ③ Bot starten
        </Alert>
      )}

      <Row className="g-4">
        <Col lg={4}>
          <Card bg="dark" text="white" className="border-secondary">
            <Card.Body>
              <h6 className="text-secondary mb-3">Allgemein</h6>
              <Form.Group className="mb-3">
                <Form.Label className="small">Vote Skip Schwellenwert: <strong>{settings.voteskip_threshold}</strong> Votes</Form.Label>
                <Form.Range min={1} max={20} value={settings.voteskip_threshold}
                  onChange={(e) => update({ voteskip_threshold: +e.target.value })} />
                <Form.Text className="text-muted">Wie viele Votes braucht es um den aktuellen Song zu skippen?</Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card bg="dark" text="white" className="border-secondary">
            <Card.Body>
              <CommandEditor settings={settings} onChange={update} />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Button variant="success" onClick={save} disabled={saving} className="mt-3">
        {saving ? 'Speichere…' : '💾 Einstellungen speichern'}
      </Button>
    </div>
  );
}
